import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { CaretLeft, Check, X } from '@phosphor-icons/react';
import { DEVOTIONAL_IMAGES } from '../config/devotionalImages';

const API = process.env.REACT_APP_BACKEND_URL;

const DevotionalStoryPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeader } = useAuth();
  
  const [devotional, setDevotional] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSegment, setCurrentSegment] = useState(0);
  const [showQuiz, setShowQuiz] = useState(false);
  const [quizIndex, setQuizIndex] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState({});
  const [showFeedback, setShowFeedback] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showCompletion, setShowCompletion] = useState(false);
  const [finalScore, setFinalScore] = useState(null);
  const [touchStart, setTouchStart] = useState(null);

  useEffect(() => {
    fetchDevotional();
  }, [id]);

  const fetchDevotional = async () => {
    try {
      const headers = user ? getAuthHeader() : {};
      const res = await axios.get(`${API}/api/devotionals/${id}`, { headers });
      setDevotional(res.data);
    } catch (error) {
      navigate('/devotionals');
    } finally {
      setLoading(false);
    }
  };

  const markSegmentRead = async (segmentId) => {
    if (!user) return;
    try {
      await axios.post(
        `${API}/api/devotionals/${id}/segment/${segmentId}/read`,
        {},
        { headers: getAuthHeader() }
      );
    } catch (error) {
    }
  };

  const submitQuizAnswer = async (questionId, answerIndex) => {
    if (!user) return null;
    try {
      const res = await axios.post(
        `${API}/api/devotionals/${id}/quiz/submit?question_id=${questionId}&answer_index=${answerIndex}`,
        {},
        { headers: getAuthHeader() }
      );
      return res.data;
    } catch (error) {
      return null;
    }
  };

  const completeDevotional = async () => {
    if (!user) return;
    try {
      const res = await axios.post(
        `${API}/api/devotionals/${id}/complete`,
        {},
        { headers: getAuthHeader() }
      );
      setFinalScore(res.data);
      setShowCompletion(true);
    } catch (error) {
    }
  };

  const handleNext = useCallback(() => {
    if (showFeedback) return; // Wait for feedback to be dismissed
    
    if (!showQuiz) {
      const segment = devotional.segments[currentSegment];
      markSegmentRead(segment.id);
      
      if (currentSegment < devotional.segments.length - 1) {
        setCurrentSegment(prev => prev + 1);
      } else {
        setShowQuiz(true);
      }
    } else {
      if (quizIndex < devotional.quiz.length - 1) {
        setQuizIndex(prev => prev + 1);
        setShowFeedback(null);
        setSelectedAnswer(null);
      } else {
        completeDevotional();
      }
    }
  }, [showQuiz, currentSegment, quizIndex, showFeedback, devotional]);

  const handlePrev = useCallback(() => {
    if (showFeedback) return;
    
    if (!showQuiz) {
      if (currentSegment > 0) {
        setCurrentSegment(prev => prev - 1);
      }
    } else {
      if (quizIndex > 0) {
        setQuizIndex(prev => prev - 1);
        setShowFeedback(null);
        setSelectedAnswer(null);
      } else {
        setShowQuiz(false);
        setCurrentSegment(devotional.segments.length - 1);
      }
    }
  }, [showQuiz, currentSegment, quizIndex, showFeedback, devotional]);

  // Touch swipe handling
  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    
    const touchEnd = e.changedTouches[0].clientX;
    const diff = touchStart - touchEnd;
    
    if (Math.abs(diff) > 50) {
      if (diff > 0) {
        handleNext();
      } else {
        handlePrev();
      }
    }
    setTouchStart(null);
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrev();
      } else if (e.key === 'Escape') {
        navigate('/devotionals');
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, navigate]);

  const handleAnswerSelect = async (answerIndex) => {
    if (showFeedback) return;
    
    setSelectedAnswer(answerIndex);
    const question = devotional.quiz[quizIndex];
    const result = await submitQuizAnswer(question.id, answerIndex);
    
    if (result) {
      setQuizAnswers(prev => ({
        ...prev,
        [question.id]: { answer: answerIndex, correct: result.is_correct }
      }));
      setShowFeedback(result);
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#0F0E0C] flex items-center justify-center">
        <div className="animate-pulse text-[#E8E4DD] font-satoshi">Loading...</div>
      </div>
    );
  }

  if (!devotional) return null;

  const totalSteps = devotional.segments.length + devotional.quiz.length;
  const currentStep = showQuiz 
    ? devotional.segments.length + quizIndex 
    : currentSegment;

  // Completion Screen
  if (showCompletion) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="fixed inset-0 bg-[#0F0E0C] flex flex-col items-center justify-center p-6"
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="text-center max-w-md"
        >
          {/* Character Image */}
          <div className="w-32 h-32 mx-auto mb-6 rounded-2xl overflow-hidden">
            <div 
              className="w-full h-full bg-cover bg-center"
              style={{ 
                backgroundImage: `url(${DEVOTIONAL_IMAGES[devotional.character_image] || DEVOTIONAL_IMAGES.david})`,
                backgroundColor: '#2A2724'
              }}
            />
          </div>

          {/* Gold Badge */}
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#B8932B]/20 border border-[#B8932B]/40 rounded-full mb-6">
            <Check size={16} className="text-[#B8932B]" weight="bold" />
            <span className="text-[#B8932B] font-satoshi text-sm font-medium">
              Leader Unlocked
            </span>
          </div>

          <h2 className="font-instrument text-3xl text-[#E8E4DD] mb-4">
            {devotional.character}
          </h2>

          <p className="font-instrument italic text-[#E8E4DD]/70 text-lg mb-8">
            "{devotional.powr_principle}"
          </p>

          {/* Score */}
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6 mb-8">
            <p className="text-[#9A958E] font-satoshi text-sm mb-2">Quiz Score</p>
            <p className="text-4xl font-instrument text-[#E8E4DD]">
              {finalScore?.quiz_score || 0}%
            </p>
            <p className="text-[#9A958E] font-satoshi text-sm mt-1">
              {finalScore?.correct_answers || 0} of {finalScore?.total_questions || 0} correct
            </p>
          </div>

          <button
            onClick={() => navigate('/devotionals')}
            className="btn-devotional btn-devotional-gold w-full"
          >
            Continue
          </button>
        </motion.div>
      </motion.div>
    );
  }

  return (
    <div 
      className="fixed inset-0 bg-[#0F0E0C] overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      {/* Background Image with Ken Burns */}
      <motion.div
        key={showQuiz ? 'quiz' : currentSegment}
        initial={{ scale: 1 }}
        animate={{ scale: 1.04 }}
        transition={{ duration: 6, ease: 'easeOut' }}
        className="absolute inset-0"
      >
        <div 
          className="w-full h-full bg-cover bg-center"
          style={{ 
            backgroundImage: `url(${DEVOTIONAL_IMAGES[devotional.character_image] || DEVOTIONAL_IMAGES.david})`,
            backgroundColor: '#1A1714'
          }}
        />
      </motion.div>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/50 to-transparent" />

      {/* Top Bar */}
      <div className="absolute top-0 left-0 right-0 p-4 flex items-center justify-between z-20">
        <button 
          onClick={() => navigate('/devotionals')}
          className="w-10 h-10 rounded-full bg-white/10 backdrop-blur-md flex items-center justify-center"
        >
          <CaretLeft size={20} className="text-white" />
        </button>

        <p className="font-satoshi text-sm text-white/70">
          {devotional.scripture_reference}
        </p>

        {/* Progress Dots */}
        <div className="flex gap-1.5">
          {Array.from({ length: Math.min(totalSteps, 8) }).map((_, i) => {
            const stepIndex = Math.floor((i / 8) * totalSteps);
            return (
              <div 
                key={i} 
                className={`progress-dot ${
                  currentStep >= stepIndex ? 'progress-dot-filled' : 'progress-dot-empty'
                }`}
              />
            );
          })}
        </div>
      </div>

      {/* Content Area */}
      <div className="absolute bottom-0 left-0 right-0 z-10">
        <AnimatePresence mode="wait">
          {!showQuiz ? (
            // Story Segment
            <motion.div
              key={`segment-${currentSegment}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="p-6 pb-8"
            >
              {/* POWR Principle - Only on first slide */}
              {currentSegment === 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="inline-block mb-6"
                >
                  <div className="px-4 py-2 bg-[#B8932B]/20 border border-[#B8932B]/40 rounded-full">
                    <p className="font-instrument italic text-[#B8932B] text-sm">
                      "{devotional.powr_principle}"
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Segment Title with Decorative Element */}
              <div className="flex items-center gap-3 mb-5">
                <div className="w-1 h-8 bg-gradient-to-b from-[#B8932B] to-[#B8932B]/20 rounded-full" />
                <h2 className="font-instrument text-2xl md:text-3xl text-white">
                  {devotional.segments[currentSegment].title}
                </h2>
              </div>

              {/* Segment Content - Enhanced Typography */}
              <div className="max-h-[45vh] overflow-y-auto pr-2 scrollbar-hide">
                <div className="space-y-4">
                  {devotional.segments[currentSegment].content.split('\n\n').map((paragraph, idx) => {
                    // Check if it's a scripture reference (usually in quotes or starts with a book name)
                    const isScripture = paragraph.match(/^["'].*["']$/) || 
                                       paragraph.match(/^(Genesis|Exodus|Leviticus|Numbers|Deuteronomy|Joshua|Judges|Ruth|Samuel|Kings|Chronicles|Ezra|Nehemiah|Esther|Job|Psalm|Proverbs|Ecclesiastes|Song|Isaiah|Jeremiah|Lamentations|Ezekiel|Daniel|Hosea|Joel|Amos|Obadiah|Jonah|Micah|Nahum|Habakkuk|Zephaniah|Haggai|Zechariah|Malachi|Matthew|Mark|Luke|John|Acts|Romans|Corinthians|Galatians|Ephesians|Philippians|Colossians|Thessalonians|Timothy|Titus|Philemon|Hebrews|James|Peter|Jude|Revelation)/i);
                    
                    // Check if it's a key insight (starts with dash or bullet)
                    const isKeyPoint = paragraph.trim().startsWith('-') || paragraph.trim().startsWith('•');
                    
                    // Check if it's a reflection prompt
                    const isReflection = paragraph.toLowerCase().includes('ask yourself') || 
                                        paragraph.toLowerCase().includes('consider this') ||
                                        paragraph.toLowerCase().includes('reflect on');

                    if (isScripture) {
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="relative pl-4 py-3 border-l-2 border-[#B8932B] bg-[#B8932B]/5 rounded-r-lg"
                        >
                          <p className="font-instrument italic text-[#B8932B] text-lg leading-relaxed">
                            {paragraph}
                          </p>
                        </motion.div>
                      );
                    }

                    if (isKeyPoint) {
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, x: -10 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className="flex items-start gap-3"
                        >
                          <div className="w-2 h-2 mt-2.5 rounded-full bg-[#B8932B] flex-shrink-0" />
                          <p className="font-satoshi text-white/90 leading-relaxed">
                            {paragraph.replace(/^[-•]\s*/, '')}
                          </p>
                        </motion.div>
                      );
                    }

                    if (isReflection) {
                      return (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, scale: 0.98 }}
                          animate={{ opacity: 1, scale: 1 }}
                          transition={{ delay: idx * 0.1 }}
                          className="bg-white/5 border border-white/10 rounded-xl p-4 mt-4"
                        >
                          <p className="font-satoshi text-white/80 leading-relaxed italic">
                            {paragraph}
                          </p>
                        </motion.div>
                      );
                    }

                    // Regular paragraph with staggered animation
                    return (
                      <motion.p
                        key={idx}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: idx * 0.08 }}
                        className="font-satoshi text-white/90 leading-relaxed text-base md:text-lg"
                      >
                        {paragraph.split(/(\*\*.*?\*\*)/).map((part, i) => {
                          // Handle bold text
                          if (part.startsWith('**') && part.endsWith('**')) {
                            return (
                              <span key={i} className="font-bold text-white">
                                {part.replace(/\*\*/g, '')}
                              </span>
                            );
                          }
                          return part;
                        })}
                      </motion.p>
                    );
                  })}
                </div>
              </div>

              {/* Continue Button with Progress Hint */}
              <div className="mt-6 space-y-3">
                <button
                  onClick={handleNext}
                  className="w-full btn-devotional btn-devotional-outline group"
                >
                  <span className="flex items-center justify-center gap-2">
                    {currentSegment < devotional.segments.length - 1 ? (
                      <>
                        Continue
                        <motion.span
                          animate={{ x: [0, 4, 0] }}
                          transition={{ repeat: Infinity, duration: 1.5 }}
                        >
                          →
                        </motion.span>
                      </>
                    ) : (
                      <>
                        Start Quiz
                        <span className="text-[#B8932B]">✦</span>
                      </>
                    )}
                  </span>
                </button>
                <p className="text-center text-white/40 text-xs font-satoshi">
                  {currentSegment + 1} of {devotional.segments.length} • Swipe or tap to continue
                </p>
              </div>
            </motion.div>
          ) : (
            // Quiz Question
            <motion.div
              key={`quiz-${quizIndex}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.4 }}
              className="p-6 pb-8"
            >
              <p className="text-[#B8932B] font-satoshi text-sm mb-4">
                Question {quizIndex + 1} of {devotional.quiz.length}
              </p>

              <h2 className="font-instrument text-xl md:text-2xl text-white mb-6">
                {devotional.quiz[quizIndex].question}
              </h2>

              {/* Answer Options */}
              <div className="space-y-3">
                {devotional.quiz[quizIndex].options.map((option, idx) => {
                  let stateClass = '';
                  if (showFeedback) {
                    if (idx === showFeedback.correct_answer) {
                      stateClass = 'quiz-answer-correct';
                    } else if (idx === selectedAnswer && !showFeedback.is_correct) {
                      stateClass = 'quiz-answer-incorrect';
                    }
                  }

                  return (
                    <motion.button
                      key={idx}
                      onClick={() => handleAnswerSelect(idx)}
                      disabled={showFeedback !== null}
                      className={`quiz-answer w-full text-left ${stateClass}`}
                      whileTap={!showFeedback ? { scale: 0.98 } : {}}
                    >
                      <span className="font-satoshi text-white/90">{option}</span>
                    </motion.button>
                  );
                })}
              </div>

              {/* Feedback */}
              <AnimatePresence>
                {showFeedback && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className={`mt-4 p-4 rounded-xl ${
                      showFeedback.is_correct 
                        ? 'bg-emerald-500/20 border border-emerald-500/40' 
                        : 'bg-red-500/20 border border-red-500/40'
                    }`}
                  >
                    <div className="flex items-center gap-2 mb-2">
                      {showFeedback.is_correct ? (
                        <Check size={18} className="text-emerald-400" weight="bold" />
                      ) : (
                        <X size={18} className="text-red-400" weight="bold" />
                      )}
                      <span className={`font-satoshi font-medium ${
                        showFeedback.is_correct ? 'text-emerald-400' : 'text-red-400'
                      }`}>
                        {showFeedback.is_correct ? 'Correct!' : 'Not quite'}
                      </span>
                    </div>
                    <p className="font-satoshi text-white/70 text-sm">
                      {showFeedback.explanation}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Continue Button (after feedback) */}
              {showFeedback && (
                <motion.button
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  onClick={handleNext}
                  className="mt-4 w-full btn-devotional btn-devotional-gold"
                >
                  {quizIndex < devotional.quiz.length - 1 ? 'Next Question' : 'Complete'}
                </motion.button>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default DevotionalStoryPage;
